import { all, put, fork, takeEvery, select, delay } from 'redux-saga/effects';
import actions from './actions';

import { get, post } from '@iso/lib/helpers/resApiRequestor';

export function* fetchSRFTransactionDetailsRequestByID() {
  yield takeEvery(actions.FETCH_SRF_TRANSACTION_DETAILS_REQUEST_BY_ID, function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_SRF_TRANSACTION_DETAILS_REQUEST_BY_ID,
        loading: true
      });

      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchSRFTransactionDetailsRequestByIDAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.FETCH_SRF_TRANSACTION_DETAILS_REQUEST_BY_ID_SUCCESS,
          loading: false,
          message: result.message,
          result: result ? result.data : null
        });
      } else {
        yield put({
          type: actions.FETCH_SRF_TRANSACTION_DETAILS_REQUEST_BY_ID_FAILED,
          message: result.message,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_SRF_TRANSACTION_DETAILS_REQUEST_BY_ID_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* fetchMerchantSettlementPaymentChannelsById() {
  yield takeEvery('FETCH_MERCHANT_SETTLEMENT_PAYMENT_CHANNELS_BY_ID', function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_MERCHANT_SETTLEMENT_PAYMENT_CHANNELS_BY_ID,
        loading: true
      });

      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchMerchantSettlementPaymentChannelsByIdAPI(
        payload,
        roleHeaderAuth
      );

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_PAYMENT_CHANNELS_BY_ID_SUCCESS,
          loading: false,
          message: result.message,
          result: result ? result.data : null
        });
      } else {
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_PAYMENT_CHANNELS_BY_ID_FAILED,
          message: result.message,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_MERCHANT_SETTLEMENT_PAYMENT_CHANNELS_BY_ID_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* fetchSettlementReportHistory() {
  yield takeEvery('FETCH_SETTLEMENT_REPORT_HISTORY', function* ({ payload, pagination }) {
    try {
      const loadmore = pagination.loadMore;
      if (loadmore) {
        yield put({
          type: actions.LOADING_MORE_SETTLEMENT_REPORT_HISTORY
        });
      } else {
        yield put({
          type: actions.FETCHING_SETTLEMENT_REPORT_HISTORY,
          loading: true
        });
      }
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchSettlementReportHistoryAPI(payload, pagination, roleHeaderAuth);
      const currentList = pagination.currentData && loadmore ? pagination.currentData : [];

      const result = apiResult.data;

      const parseData =
        currentList && currentList.length > 0
          ? currentList.concat(result.data.logs)
          : result.data.logs;

      const finalData =
        parseData && parseData.length > 0
          ? parseData.sort((a, b) => {
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB - dateA;
            })
          : [];

      if (result.success) {
        yield put({
          type: actions.FETCH_SETTLEMENT_REPORT_HISTORY_SUCCESS,
          loading: false,
          result: finalData,
          pagination: result ? result.data.pagination : null
        });
      } else {
        yield put({
          type: actions.FETCH_SETTLEMENT_REPORT_HISTORY_FAILED,
          loading: false,
          payload: currentList
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_SETTLEMENT_REPORT_HISTORY_FAILED,
        loading: false,
        payload: []
      });
    }
  });
}

export function* generateMerchantSettlementReport() {
  yield takeEvery(actions.GENERATE_MERCHANT_SETTLEMENT_REPORT, function* ({ payload }) {
    try {
      yield put({
        type: actions.GENERATING_MERCHANT_SETTLEMENT_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield generateMerchantSettlementReportAPI(payload, roleHeaderAuth);

      const result = apiResult.data;
      if (result.success) {
        yield put({
          type: actions.GENERATE_MERCHANT_SETTLEMENT_REPORT_SUCCESS,
          loading: false,
          message: result.message
        });
        yield put({
          type: actions.FETCH_SETTLEMENT_SUBMISSIONS,
          payload: {
            merchant_settlement_cycle_id: payload.merchant_settlement_cycle_id,
            page: 0,
            limit: 5
          }
        });
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.GENERATE_MERCHANT_SETTLEMENT_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.GENERATE_MERCHANT_SETTLEMENT_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.GENERATE_MERCHANT_SETTLEMENT_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* fetchSettlementSubmissions() {
  yield takeEvery(actions.FETCH_SETTLEMENT_SUBMISSIONS, function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_SETTLEMENT_SUBMISSIONS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchSettlementSubmissionsAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      const finalData = result.data.settlement_submissions;

      if (result.success) {
        yield put({
          type: actions.FETCH_SETTLEMENT_SUBMISSIONS_SUCCESS,
          loading: false,
          result: finalData,
          pagination: result ? result.data.pagination : null,
          summary: result?.data?.summary
        });
      } else {
        yield put({
          type: actions.FETCH_SETTLEMENT_SUBMISSIONS_FAILED,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_SETTLEMENT_SUBMISSIONS_FAILED,
        loading: false
      });
    }
  });
}

export function* fetchSettlementSubmission() {
  yield takeEvery(actions.FETCH_SETTLEMENT_SUBMISSION, function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_SETTLEMENT_SUBMISSION,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchSettlementSubmissionsAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      const finalData = result.data.settlement_submissions?.[0];

      if (result.success) {
        yield put({
          type: actions.FETCH_SETTLEMENT_SUBMISSION_SUCCESS,
          loading: false,
          result: finalData
        });
      } else {
        yield put({
          type: actions.FETCH_SETTLEMENT_SUBMISSION_FAILED,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_SETTLEMENT_SUBMISSION_FAILED,
        loading: false
      });
    }
  });
}

export function* downloadSettlementSubmissionDocument() {
  yield takeEvery(actions.DOWNLOAD_SETTLEMENT_SUBMISSION_DOCUMENT, function* ({ id }) {
    try {
      yield put({
        type: actions.DOWNLOADING_SETTLEMENT_SUBMISSION_DOCUMENT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield downloadSettlementSubmissionDocumentAPI(id, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.DOWNLOAD_SETTLEMENT_SUBMISSION_DOCUMENT_SUCCESS,
          loading: false,
          message: 'Attachment downloaded successfully.'
        });
        if (result.url) {
          window.open(result.url, '_blank');
        }
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.DOWNLOAD_SETTLEMENT_SUBMISSION_DOCUMENT_FAILED,
            message: `Endpoint not found`,
            loading: false
          });
        } else {
          yield put({
            type: actions.DOWNLOAD_SETTLEMENT_SUBMISSION_DOCUMENT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.DOWNLOAD_SETTLEMENT_SUBMISSION_DOCUMENT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* approveSettlementSubmission() {
  yield takeEvery(actions.APPROVE_SETTLEMENT_SUBMISSION, function* ({ urls, payload }) {
    try {
      yield put({
        type: actions.APPROVING_SETTLEMENT_SUBMISSION,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();

      let eSignature = [];
      if (
        payload &&
        payload.e_signature &&
        urls &&
        urls.e_signature &&
        payload.e_signature.length === urls.e_signature.length
      ) {
        const dataFile = payload.e_signature;
        dataFile.forEach(file => {
          if (urls.e_signature.some(str => str.includes(file.name))) {
            let getURL = urls.e_signature.find(str => str.includes(file.name));
            if (getURL) {
              uploadFileToS3(getURL, file);
              let currentObj = {
                name: file.name,
                file_type: file.file_type
              };
              eSignature.push(currentObj);
            }
          }
        });

        yield delay(urls.e_signature.length * 1000);
      }

      const apiResult = yield approveSettlementSubmissionAPI(
        { ...payload, e_signature: eSignature },
        roleHeaderAuth
      );

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.APPROVE_SETTLEMENT_SUBMISSION_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.APPROVE_SETTLEMENT_SUBMISSION_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.APPROVE_SETTLEMENT_SUBMISSION_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* endorseSettlementSubmission() {
  yield takeEvery(actions.ENDORSE_SETTLEMENT_SUBMISSION, function* ({ urls, payload }) {
    try {
      yield put({
        type: actions.ENDORSING_SETTLEMENT_SUBMISSION,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();

      let eSignature = [];
      if (
        payload &&
        payload.e_signature &&
        urls &&
        urls.e_signature &&
        payload.e_signature.length === urls.e_signature.length
      ) {
        const dataFile = payload.e_signature;
        dataFile.forEach(file => {
          if (urls.e_signature.some(str => str.includes(file.name))) {
            let getURL = urls.e_signature.find(str => str.includes(file.name));
            if (getURL) {
              uploadFileToS3(getURL, file);
              let currentObj = {
                name: file.name,
                file_type: file.file_type
              };
              eSignature.push(currentObj);
            }
          }
        });

        yield delay(urls.e_signature.length * 1000);
      }

      const apiResult = yield endorseSettlementSubmissionAPI(
        { ...payload, e_signature: eSignature },
        roleHeaderAuth
      );

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.ENDORSE_SETTLEMENT_SUBMISSION_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.ENDORSE_SETTLEMENT_SUBMISSION_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.ENDORSE_SETTLEMENT_SUBMISSION_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* rejectSettlementSubmission() {
  yield takeEvery(actions.REJECT_SETTLEMENT_SUBMISSION, function* ({ payload }) {
    try {
      yield put({
        type: actions.REJECTING_SETTLEMENT_SUBMISSION,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield rejectSettlementSubmissionAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.REJECT_SETTLEMENT_SUBMISSION_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.REJECT_SETTLEMENT_SUBMISSION_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.REJECT_SETTLEMENT_SUBMISSION_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* approveSettlementSubmissions() {
  yield takeEvery(actions.APPROVE_SETTLEMENT_SUBMISSIONS, function* ({ urls, payload }) {
    try {
      yield put({
        type: actions.APPROVING_SETTLEMENT_SUBMISSIONS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();

      let eSignature = [];
      if (
        payload &&
        payload.e_signature &&
        urls &&
        urls.e_signature &&
        payload.e_signature.length === urls.e_signature.length
      ) {
        const dataFile = payload.e_signature;
        dataFile.forEach(file => {
          if (urls.e_signature.some(str => str.includes(file.name))) {
            let getURL = urls.e_signature.find(str => str.includes(file.name));
            if (getURL) {
              uploadFileToS3(getURL, file);
              let currentObj = {
                name: file.name,
                file_type: file.file_type
              };
              eSignature.push(currentObj);
            }
          }
        });

        yield delay(urls.e_signature.length * 1000);
      }

      const apiResult = yield approveSettlementSubmissionsAPI(
        { ...payload, e_signature: eSignature },
        roleHeaderAuth
      );

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.APPROVE_SETTLEMENT_SUBMISSIONS_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.APPROVE_SETTLEMENT_SUBMISSIONS_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.APPROVE_SETTLEMENT_SUBMISSIONS_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* endorseSettlementSubmissions() {
  yield takeEvery(actions.ENDORSE_SETTLEMENT_SUBMISSIONS, function* ({ urls, payload }) {
    try {
      yield put({
        type: actions.ENDORSING_SETTLEMENT_SUBMISSIONS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();

      let eSignature = [];
      if (
        payload &&
        payload.e_signature &&
        urls &&
        urls.e_signature &&
        payload.e_signature.length === urls.e_signature.length
      ) {
        const dataFile = payload.e_signature;
        dataFile.forEach(file => {
          if (urls.e_signature.some(str => str.includes(file.name))) {
            let getURL = urls.e_signature.find(str => str.includes(file.name));
            if (getURL) {
              uploadFileToS3(getURL, file);
              let currentObj = {
                name: file.name,
                file_type: file.file_type
              };
              eSignature.push(currentObj);
            }
          }
        });

        yield delay(urls.e_signature.length * 1000);
      }

      const apiResult = yield endorseSettlementSubmissionsAPI(
        { ...payload, e_signature: eSignature },
        roleHeaderAuth
      );

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.ENDORSE_SETTLEMENT_SUBMISSIONS_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.ENDORSE_SETTLEMENT_SUBMISSIONS_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.ENDORSE_SETTLEMENT_SUBMISSIONS_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* rejectSettlementSubmissions() {
  yield takeEvery(actions.REJECT_SETTLEMENT_SUBMISSIONS, function* ({ payload }) {
    try {
      yield put({
        type: actions.REJECTING_SETTLEMENT_SUBMISSIONS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield rejectSettlementSubmissionsAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.REJECT_SETTLEMENT_SUBMISSIONS_SUCCESS,
          loading: false,
          message: result.message,
          result: result.data,
          payload: payload
        });
      } else {
        yield put({
          type: actions.REJECT_SETTLEMENT_SUBMISSIONS_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.REJECT_SETTLEMENT_SUBMISSIONS_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* generateConsolidatedSettlementReport() {
  yield takeEvery(actions.GENERATE_CONSOLIDATED_SETTLEMENT_REPORT, function* ({ payload }) {
    try {
      yield put({
        type: actions.GENERATING_CONSOLIDATED_SETTLEMENT_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield generateConsolidatedSettlementReportAPI(payload, roleHeaderAuth);

      const result = apiResult.data;
      if (result.success) {
        yield put({
          type: actions.GENERATE_CONSOLIDATED_SETTLEMENT_REPORT_SUCCESS,
          loading: false,
          message: result.message
        });
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.GENERATE_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.GENERATE_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.GENERATE_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* fetchConsolidatedSettlementReports() {
  yield takeEvery(actions.FETCH_CONSOLIDATED_SETTLEMENT_REPORTS, function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_CONSOLIDATED_SETTLEMENT_REPORTS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchConsolidatedSettlementReportsAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.FETCH_CONSOLIDATED_SETTLEMENT_REPORTS_SUCCESS,
          loading: false,
          result: result ? result.data : null,
          pagination: result ? result.pagination : null
        });
      } else {
        yield put({
          type: actions.FETCH_CONSOLIDATED_SETTLEMENT_REPORTS_FAILED,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_CONSOLIDATED_SETTLEMENT_REPORTS_FAILED,
        loading: false
      });
    }
  });
}

export function* downloadConsolidatedSettlementReport() {
  yield takeEvery(actions.DOWNLOAD_CONSOLIDATED_SETTLEMENT_REPORT, function* ({ id }) {
    try {
      yield put({
        type: actions.DOWNLOADING_CONSOLIDATED_SETTLEMENT_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield downloadConsolidatedSettlementReportAPI(id, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.DOWNLOAD_CONSOLIDATED_SETTLEMENT_REPORT_SUCCESS,
          loading: false,
          message: 'Report downloaded successfully.'
        });

        if (result.url) {
          window.location.href = result.url;
        }
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.DOWNLOAD_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.DOWNLOAD_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.DOWNLOAD_CONSOLIDATED_SETTLEMENT_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* generateMerchantSettlementSummaryReport() {
  yield takeEvery(actions.GENERATE_MERCHANT_SETTLEMENT_SUMMARY_REPORT, function* ({ payload }) {
    try {
      yield put({
        type: actions.GENERATING_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield generateMerchantSettlementSummaryReportAPI(payload, roleHeaderAuth);

      const result = apiResult.data;
      if (result.success) {
        yield put({
          type: actions.GENERATE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_SUCCESS,
          loading: false,
          message: result.message
        });
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS,
          payload: {
            page: 0,
            limit: 20
          }
        });
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.GENERATE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.GENERATE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.GENERATE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* downloadMerchantSettlementSummaryReport() {
  yield takeEvery(actions.DOWNLOAD_MERCHANT_SETTLEMENT_SUMMARY_REPORT, function* ({ id }) {
    try {
      yield put({
        type: actions.DOWNLOADING_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield downloadMerchantSettlementSummaryReportAPI(id, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.DOWNLOAD_MERCHANT_SETTLEMENT_SUMMARY_REPORT_SUCCESS,
          loading: false,
          message: 'Settlement summary report downloaded successfully.'
        });

        if (result.url) {
          window.location.href = result.url;
        }
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.DOWNLOAD_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.DOWNLOAD_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.DOWNLOAD_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* fetchMerchantSettlementSummaryReportData() {
  yield takeEvery(actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS, function* ({ payload }) {
    try {
      yield put({
        type: actions.FETCHING_MERCHANT_SETTLEMENT_SUMMARY_REPORTS,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield fetchMerchantSettlementSummaryReportDataAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS_SUCCESS,
          loading: false,
          result: result ? result.data : null,
          pagination: result ? result.pagination : null,
          filter: payload
        });
      } else {
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS_FAILED,
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS_FAILED,
        loading: false
      });
    }
  });
}

export function* approveMerchantSettlementSummaryReport() {
  yield takeEvery(
    actions.APPROVE_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
    function* ({ urls, payload }) {
      try {
        yield put({
          type: actions.APPROVING_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
          loading: true
        });
        const roleHeaderAuth = yield getRoleHeader();

        let eSignature = [];
        if (
          payload &&
          payload.e_signature &&
          urls &&
          urls.e_signature &&
          payload.e_signature.length === urls.e_signature.length
        ) {
          const dataFile = payload.e_signature;
          dataFile.forEach(file => {
            if (urls.e_signature.some(str => str.includes(file.name))) {
              let getURL = urls.e_signature.find(str => str.includes(file.name));
              if (getURL) {
                uploadFileToS3(getURL, file);
                let currentObj = {
                  name: file.name,
                  file_type: file.file_type
                };
                eSignature.push(currentObj);
              }
            }
          });

          yield delay(urls.e_signature.length * 1000);
        }

        const apiResult = yield approveMerchantSettlementSummaryReportAPI(
          { ...payload, e_signature: eSignature },
          roleHeaderAuth
        );

        const result = apiResult.data;

        const state = yield select();
        const pagination = state.Settlement.merchantSettlementSummaryReportsPagination ?? {
          page: 0,
          limit: 20
        };
        const filter = state.Settlement.merchantSettlementSummaryReportsFilter ?? {};

        if (result.success) {
          yield put({
            type: actions.APPROVE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_SUCCESS,
            loading: false,
            message: result.message
          });
          yield put({
            type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS,
            payload: { ...filter, ...pagination }
          });
        } else {
          yield put({
            type: actions.APPROVE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: 'Failed to process request',
            loading: false
          });
        }
      } catch (error) {
        yield put({
          type: actions.APPROVE_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    }
  );
}

export function* requestReviewMerchantSettlementSummaryReport() {
  yield takeEvery(
    actions.REQUEST_REVIEW_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
    function* ({ urls, payload }) {
      try {
        yield put({
          type: actions.REQUESTING_REVIEW_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
          loading: true
        });
        const roleHeaderAuth = yield getRoleHeader();

        let eSignature = [];
        if (
          payload &&
          payload.e_signature &&
          urls &&
          urls.e_signature &&
          payload.e_signature.length === urls.e_signature.length
        ) {
          const dataFile = payload.e_signature;
          dataFile.forEach(file => {
            if (urls.e_signature.some(str => str.includes(file.name))) {
              let getURL = urls.e_signature.find(str => str.includes(file.name));
              if (getURL) {
                uploadFileToS3(getURL, file);
                let currentObj = {
                  name: file.name,
                  file_type: file.file_type
                };
                eSignature.push(currentObj);
              }
            }
          });

          yield delay(urls.e_signature.length * 1000);
        }

        const apiResult = yield requestReviewMerchantSettlementReportAPI(
          { ...payload, e_signature: eSignature },
          roleHeaderAuth
        );

        const result = apiResult.data;

        const state = yield select();
        const pagination = state.Settlement.merchantSettlementSummaryReportsPagination ?? {
          page: 0,
          limit: 20
        };
        const filter = state.Settlement.merchantSettlementSummaryReportsFilter ?? {};

        if (result.success) {
          yield put({
            type: actions.REQUEST_REVIEW_MERCHANT_SETTLEMENT_SUMMARY_REPORT_SUCCESS,
            loading: false,
            message: result.message
          });
          yield put({
            type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS,
            payload: { ...filter, ...pagination }
          });
        } else {
          yield put({
            type: actions.REQUEST_REVIEW_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
            message: 'Failed to process request',
            loading: false
          });
        }
      } catch (error) {
        yield put({
          type: actions.REQUEST_REVIEW_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    }
  );
}

export function* rejectMerchantSettlementSummaryReport() {
  yield takeEvery(actions.REJECT_MERCHANT_SETTLEMENT_SUMMARY_REPORT, function* ({ payload }) {
    try {
      yield put({
        type: actions.REJECTING_MERCHANT_SETTLEMENT_SUMMARY_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield rejectMerchantSettlementSummaryReportAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      const state = yield select();
      const pagination = state.Settlement.merchantSettlementSummaryReportsPagination ?? {
        page: 0,
        limit: 20
      };
      const filter = state.Settlement.merchantSettlementSummaryReportsFilter ?? {};

      if (result.success) {
        yield put({
          type: actions.REJECT_MERCHANT_SETTLEMENT_SUMMARY_REPORT_SUCCESS,
          loading: false,
          message: result.message
        });
        yield put({
          type: actions.FETCH_MERCHANT_SETTLEMENT_SUMMARY_REPORTS,
          payload: { ...filter, ...pagination }
        });
      } else {
        yield put({
          type: actions.REJECT_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
          message: 'Failed to process request',
          loading: false
        });
      }
    } catch (error) {
      yield put({
        type: actions.REJECT_MERCHANT_SETTLEMENT_SUMMARY_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

export function* sendEmailValidationReport() {
  yield takeEvery(actions.SEND_EMAIL_VALIDATION_REPORT, function* ({ payload }) {
    try {
      yield put({
        type: actions.SENDING_EMAIL_VALIDATION_REPORT,
        loading: true
      });
      const roleHeaderAuth = yield getRoleHeader();
      const apiResult = yield sendEmailValidationReportAPI(payload, roleHeaderAuth);

      const result = apiResult.data;

      if (result.success) {
        yield put({
          type: actions.SEND_EMAIL_VALIDATION_REPORT_SUCCESS,
          loading: false,
          message: result.message,
          result: result?.data ?? {},
          payload: payload
        });
      } else {
        if (result.status === 404) {
          yield put({
            type: actions.SEND_EMAIL_VALIDATION_REPORT_FAILED,
            message: `Endpoint ${result.title}`,
            loading: false
          });
        } else {
          yield put({
            type: actions.SEND_EMAIL_VALIDATION_REPORT_FAILED,
            message: result.message,
            loading: false
          });
        }
      }
    } catch (error) {
      yield put({
        type: actions.SEND_EMAIL_VALIDATION_REPORT_FAILED,
        message: 'Failed to process request',
        loading: false
      });
    }
  });
}

function* getRoleHeader() {
  const state = yield select();
  const identity = state.Auth.identity;
  const currentPage = state.App.currentPage;
  const currentAction = state.App.currentAction;
  if (currentAction) {
    return `${identity.role_id}:${currentPage.id}:${currentAction.id}`;
  } else {
    return `${identity.role_id}:${currentPage.id}`;
  }
}

async function uploadFileToS3(url, payload) {
  return await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': payload.file.type
    },
    body: payload.file
  });
}

function fetchMerchantSettlementPaymentChannelsByIdAPI(id, roleAuth) {
  return get(`merchant-settlement-cycle/${id}/payment-channels`, null, null, null, null, roleAuth);
}

function fetchSRFTransactionDetailsRequestByIDAPI(id, roleAuth) {
  return get(`merchant-settlement-cycle/${id}/report-request`, null, null, null, null, roleAuth);
}

function fetchSettlementReportHistoryAPI(data, pagination, roleAuth) {
  return get(
    `settlement-report-history/${data}`,
    null,
    pagination ? pagination : { page: 0, limit: 20 },
    null,
    null,
    roleAuth
  );
}

function generateMerchantSettlementReportAPI(data, roleAuth) {
  return post(`settlement-reports`, data, null, roleAuth);
}

function fetchSettlementSubmissionsAPI(payload, roleAuth) {
  return get(`settlement-submissions`, payload, null, null, null, roleAuth);
}

function downloadSettlementSubmissionDocumentAPI(id, roleAuth) {
  return get(`settlement-submission-files/${id}/download`, null, null, null, null, roleAuth);
}

function approveSettlementSubmissionsAPI(data, roleAuth) {
  return post(`settlement-submissions/bulk-approve`, data, null, null, roleAuth);
}
function endorseSettlementSubmissionsAPI(data, roleAuth) {
  return post(`settlement-submissions/bulk-endorse`, data, null, null, roleAuth);
}
function rejectSettlementSubmissionsAPI(data, roleAuth) {
  return post(`settlement-submissions/bulk-reject`, data, null, null, roleAuth);
}

function approveSettlementSubmissionAPI(data, roleAuth) {
  return post(`settlement-submissions/approve`, data, null, null, roleAuth);
}
function endorseSettlementSubmissionAPI(data, roleAuth) {
  return post(`settlement-submissions/endorse`, data, null, null, roleAuth);
}
function rejectSettlementSubmissionAPI(data, roleAuth) {
  return post(`settlement-submissions/reject`, data, null, null, roleAuth);
}

function generateConsolidatedSettlementReportAPI(data, roleAuth) {
  return post(`consolidated-settlement-reports`, data, null, roleAuth);
}

function fetchConsolidatedSettlementReportsAPI(payload, roleAuth) {
  return get(`consolidated-settlement-reports`, payload, null, null, null, roleAuth);
}

function downloadConsolidatedSettlementReportAPI(id, roleAuth) {
  return get(`consolidated-settlement-reports/${id}/download`, null, null, null, null, roleAuth);
}

function generateMerchantSettlementSummaryReportAPI(data, roleAuth) {
  return post(`merchant-settlement-summary-reports`, data, null, roleAuth);
}

function downloadMerchantSettlementSummaryReportAPI(id, roleAuth) {
  return get(
    `merchant-settlement-summary-reports/${id}/download`,
    null,
    null,
    null,
    null,
    roleAuth
  );
}

function fetchMerchantSettlementSummaryReportDataAPI(data, roleAuth) {
  return get(`merchant-settlement-summary-reports`, data, null, null, null, roleAuth);
}

function requestReviewMerchantSettlementReportAPI(data, roleAuth) {
  return post(`merchant-settlement-summary-reports/request-review`, data, null, null, roleAuth);
}

function approveMerchantSettlementSummaryReportAPI(data, roleAuth) {
  return post(`merchant-settlement-summary-reports/approve`, data, null, null, roleAuth);
}

function rejectMerchantSettlementSummaryReportAPI(data, roleAuth) {
  return post(`merchant-settlement-summary-reports/reject`, data, null, null, roleAuth);
}

function sendEmailValidationReportAPI(data, roleAuth) {
  return post(`settlement-submissions/send-trx-report`, data, null, null, roleAuth);
}

export default function* rootSaga() {
  yield all([
    fork(fetchSRFTransactionDetailsRequestByID),
    fork(fetchMerchantSettlementPaymentChannelsById),
    fork(fetchSettlementReportHistory),
    fork(generateMerchantSettlementReport),
    fork(fetchSettlementSubmissions),
    fork(fetchSettlementSubmission),
    fork(downloadSettlementSubmissionDocument),
    fork(approveSettlementSubmissions),
    fork(endorseSettlementSubmissions),
    fork(rejectSettlementSubmissions),
    fork(approveSettlementSubmission),
    fork(endorseSettlementSubmission),
    fork(rejectSettlementSubmission),
    fork(generateConsolidatedSettlementReport),
    fork(fetchConsolidatedSettlementReports),
    fork(downloadConsolidatedSettlementReport),
    fork(generateMerchantSettlementSummaryReport),
    fork(downloadMerchantSettlementSummaryReport),
    fork(fetchMerchantSettlementSummaryReportData),
    fork(requestReviewMerchantSettlementSummaryReport),
    fork(approveMerchantSettlementSummaryReport),
    fork(rejectMerchantSettlementSummaryReport),
    fork(sendEmailValidationReport)
  ]);
}
